import { useState } from "react";
import { Link } from "react-router-dom";
import { AuthPageLayout } from "./auth-page-layout";
import { requestPasswordReset } from "@/api/auth";
import { getAuthErrorMessage } from "@/hooks/use-auth";
import { Button, FormError, FormField, TextInput } from "@/components/ui";
import { useT } from "@/i18n";
import { authMessages } from "@/i18n/messages/auth";

export function ForgotPasswordPage() {
  const t = useT(authMessages);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthPageLayout>
      <div className="auth-page-stack">
        <h2 className="auth-page-title" data-testid="forgot-heading">
          {t("resetPassword")}
        </h2>

        {sent ? (
          <div className="auth-state-stack" data-testid="forgot-success">
            <p className="auth-page-copy">{t("checkYourEmail")}</p>
            <Link
              to="/auth/login"
              className="auth-small-link"
              data-testid="back-to-login"
            >
              {t("signIn")}
            </Link>
          </div>
        ) : (
          <>
            <FormError
              message={error}
              className="mt-0"
              data-testid="forgot-error"
            />

            <form
              onSubmit={handleSubmit}
              className="auth-login-form flex flex-col"
              data-testid="forgot-form"
            >
              <FormField label={t("email")} htmlFor="email">
                <TextInput
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("emailPlaceholder")}
                  className="auth-liquid-input h-12 rounded-[18px] px-4 text-appkit-subhead"
                  data-testid="forgot-email"
                />
              </FormField>

              <Button
                type="submit"
                variant="secondary"
                size="lg"
                disabled={isSubmitting}
                className="auth-liquid-primary-button w-full font-semibold"
                data-testid="forgot-submit"
              >
                {isSubmitting ? t("sending") : t("sendLink")}
              </Button>
            </form>

            <Link
              to="/auth/login"
              className="auth-small-link"
              data-testid="back-to-login"
            >
              {t("signIn")}
            </Link>
          </>
        )}
      </div>
    </AuthPageLayout>
  );
}
